import React, { useState } from 'react';
import styles from './Timeline.module.css';
import AbilitiesTimeline from './AbilitiesTimeline';
import { DetailedRoundData } from '../utils/types';
import PlayerDropdown from './PlayerDropdown';

interface TimelineProps {
  isPlaying: boolean;
  currentRoundTime: number;
  roundDuration: number;
  roundData?: DetailedRoundData;
  onPlayPauseToggle: () => void;
  onRoundTimeChange: (newTime: number) => void;
  onDraggingChange: (isDragging: boolean) => void;
  selectedPlayerIndices: number[];
  onPlayerSelectionsChange: (indices: number[]) => void;
  onUtilityTableOpen: () => void;
}

const Timeline: React.FC<TimelineProps> = ({
  isPlaying,
  currentRoundTime,
  roundDuration,
  roundData,
  onPlayPauseToggle,
  onRoundTimeChange,
  onDraggingChange,
  selectedPlayerIndices,
  onPlayerSelectionsChange,
  onUtilityTableOpen,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  // Get the agent name for the selected player
  const selectedAgent = roundData?.frames[0]?.players[selectedPlayerIndices[0]]?.agent?.toLowerCase() || '';

  const handleDragStart = () => {
    setIsDragging(true);
    onDraggingChange(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDraggingChange(false);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onRoundTimeChange(Number(e.target.value));
  };

  const handleSliderKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ') {
      e.preventDefault();
      onPlayPauseToggle();
    }
  };

  return (
    <div className={styles.timeControlContainer}>
      <div className={styles.timelineTopRow}>
        <div className={styles.leftColumn}>
          <div className={styles.playerDropdownContainer}>
            <PlayerDropdown
              selectedPlayerIndices={selectedPlayerIndices}
              roundData={roundData}
              onPlayerSelectionsChange={onPlayerSelectionsChange}
              onUtilityTableOpen={onUtilityTableOpen}
            />
          </div>
        </div>
        <div className={styles.rightColumn}>
          <AbilitiesTimeline 
            selectedPlayerIndices={selectedPlayerIndices}
            roundData={roundData}
            roundDuration={roundDuration}
            currentRoundTime={currentRoundTime}
            onTimeChange={onRoundTimeChange}
          />
        </div>
      </div>
      <div className={styles.timelineRow}>
        <div className={styles.leftColumnPlayPause}>
          <button onClick={onPlayPauseToggle} className={styles.playPauseButton}>
            {isPlaying ? <i className="bi bi-pause-fill"></i> : <i className="bi bi-play-fill"></i>}
          </button>
        </div>
        <div className={styles.rightColumn}>
          <input
            type="range"
            min={0}
            max={roundDuration}
            step={0.01}
            value={currentRoundTime}
            onChange={handleTimeChange}
            onMouseDown={handleDragStart}
            onMouseUp={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchEnd={handleDragEnd}
            onKeyDown={handleSliderKeyDown}
            className={styles.timeSlider}
            style={{
              '--slider-fill': `${(currentRoundTime / roundDuration) * 100.0}%`
            } as React.CSSProperties}
          />
        </div>
      </div>
    </div>
  );
};

export default Timeline;
